'use client';

import {
	UserSettings,
	isDev,
} from '../../environmentVariables/EnvironmentVariables';
import SettingsSwitch from './components/SettingsSwitch';
import Button from 'src/components/primitives/Button/Button';
import { Settings } from 'src/components/Settings/SettingsForm';
import Divider from 'src/components/primitives/Divider/Divider';
import useModal from 'src/components/Modals/hooks/useModal';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';
import { Download, Trash } from '@drift-labs/icons';
import { useTitanBadgeData } from 'src/hooks/useTitanBadge';
import TitanBadge from 'src/components/Swap/TitanBadge';
import { cj } from 'src/utils/classNames';
import { useDeleteAccount } from 'src/hooks/useDeleteAccount';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';

interface Props {
	localSettings: Settings;
	toggleSetting: (key: keyof UserSettings) => void;
	updateSettings: (settings: Settings) => void;
}

const EXPLORER_OPTIONS: BaseRadioItemProps<UserSettings['explorer']>[] = [
	{
		id: 'solana',
		value: 'solana',
		label: 'Solana Explorer',
	},
	{
		id: 'solscan',
		value: 'solscan',
		label: 'Solscan',
	},
	{
		id: 'xray',
		value: 'xray',
		label: 'XRAY',
	},
	{
		id: 'solanafm',
		value: 'solanafm',
		label: 'Solana FM',
	},
];

const SWAP_CLIENT_OPTIONS: BaseRadioItemProps<
	UserSettings['swapClientType']
>[] = [
	{
		id: 'titan',
		value: 'titan',
		label: 'Titan',
	},
	{
		id: 'jupiter',
		value: 'jupiter',
		label: 'Jupiter',
	},
];

const OtherSettings = ({
	localSettings,
	toggleSetting,
	updateSettings,
}: Props) => {
	const connected = useWalletIsConnected();
	const { push } = useModal();
	const isDevEnv = isDev();
	const { hasSubaccounts, canDelete } = useDeleteAccount();

	// Use combined hook to get both badge status and count data
	const { hasBadge, count, isLoading } = useTitanBadgeData();

	// Determine if we should show the badge section
	const shouldShowBadgeSection =
		!isLoading &&
		!hasBadge.data?.has_badge &&
		(count.data?.badge_count || 0) < 10000;

	return (
		<div className="flex flex-col gap-y-lg">
			<div className="flex flex-col gap-y-sm">
				<h2 className="typo-t3 text-text-default">Preferred Explorer</h2>
				<RadioGroup.Default
					size="md"
					variant="card"
					className="grid grid-cols-2 gap-md sm:grid-cols-4"
					value={localSettings.explorer}
					onValueChange={(value) =>
						updateSettings({ ...localSettings, explorer: value })
					}
					items={EXPLORER_OPTIONS}
				/>
			</div>
			<Divider />
			<div className="flex flex-col gap-y-lg">
				<div className="flex items-center justify-between">
					<h2 className="typo-t3 text-text-default">Swap Client</h2>
					<TitanBadge />
				</div>
				<RadioGroup.Default
					size="md"
					variant="card"
					className="grid grid-cols-2 gap-md"
					value={localSettings.swapClientType}
					onValueChange={(value) =>
						updateSettings({ ...localSettings, swapClientType: value })
					}
					items={SWAP_CLIENT_OPTIONS}
				/>
				{shouldShowBadgeSection && (
					<div
						className={cj(
							'relative flex h-20 items-center gap-x-lg overflow-hidden rounded-xs',
							'bg-gradient-to-r from-[#6C1D73] via-[#5819DC] to-[#6954EE] px-4 py-3 text-white'
						)}
					>
						<img src="/assets/images/swap/titan-badge.webp" className="h-11" />
						<div className="relative flex flex-col gap-y-1">
							<span className="typo-t4">Titan&apos;s Drift Badge</span>
							<span className="typo-b5">
								Available to the first 10k users who execute a minimum of $100
								in Drift Swap, powered by Titan.
							</span>
						</div>
					</div>
				)}
			</div>
			<Divider />
			<SettingsSwitch
				key="versioned_tx_toggle"
				label="Enable Versioned Transactions"
				className="typo-t3"
				checked={localSettings.enableVersionedTx}
				onChange={() => toggleSetting('enableVersionedTx')}
				tooltipDefinitionId="settingsVersionedTx"
			/>
			<Divider />
			<div className="flex items-center justify-between">
				<span className="typo-t3 text-text-default">
					Download Historical Data
				</span>
				<Button
					size="md"
					variant="secondary"
					onClick={() => push({ type: 'export_history', props: {} })}
					disabled={!connected}
					rightIcon={Download}
				>
					Download
				</Button>
			</div>

			{connected && (
				<>
					<Divider />
					<div className="flex flex-col gap-y-sm">
						<div className="flex items-center justify-between">
							<div className="flex flex-col gap-y-xs">
								<span className="typo-t3 text-text-default">
									Delete Account Data
								</span>
								<span className="typo-b4 text-text-secondary">
									Permanently delete your off-chain data from Drift
								</span>
							</div>
							<Tooltip
								disabled={!hasSubaccounts}
								content={'Delete all subaccounts first'}
							>
								<Button
									size="md"
									variant="negative"
									onClick={() => push({ type: 'delete_account', props: {} })}
									disabled={!canDelete}
									rightIcon={Trash}
								>
									Delete
								</Button>
							</Tooltip>
						</div>
					</div>
				</>
			)}

			{isDevEnv && (
				<>
					<Divider />
					<SettingsSwitch
						key="use-devnet"
						label="Use Devnet"
						className="typo-t3"
						checked={localSettings.devnetOverride}
						onChange={() => toggleSetting('devnetOverride')}
						tooltipContent="This switch makes the client connect to the devnet RPC and use devnet Drift SDK configs."
					/>
					<SettingsSwitch
						label="Use Staging History Server"
						className="typo-t3"
						checked={localSettings.stagingHistoryServerOverride}
						onChange={() => toggleSetting('stagingHistoryServerOverride')}
						tooltipContent="This switch makes the client use the staging HISTORY SERVER - should be used with the 'use mainnet' switch being turned on"
					/>
					<SettingsSwitch
						label="Use Staging DLOB Server"
						className="typo-t3"
						checked={localSettings.stagingDlobServerOverride}
						onChange={() => toggleSetting('stagingDlobServerOverride')}
						tooltipContent="This switch makes the client use the staging DLOB SERVER - should be used with the 'use mainnet' switch being turned on"
					/>
					<SettingsSwitch
						label="Use Staging Data API"
						className="typo-t3"
						checked={localSettings.stagingDataServerOverride}
						onChange={() => toggleSetting('stagingDataServerOverride')}
						tooltipContent="This switch makes the client use the staging DATA API - should be used with the 'use mainnet' switch being turned on"
					/>
				</>
			)}
		</div>
	);
};

export default OtherSettings;
